import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

type CallbackStatus = 'loading' | 'success' | 'error';

/**
 * Twitter OAuth callback handler.
 * Receives code and state from Twitter, exchanges for token.
 */
export function TwitterCallback() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<CallbackStatus>('loading');
  const [errorMessage, setErrorMessage] = useState('');
  const [hasProcessed, setHasProcessed] = useState(false);

  useEffect(() => {
    // Prevent double-processing in React StrictMode
    if (hasProcessed) return;
    setHasProcessed(true);

    const processCallback = async () => {
      // Check for error from Twitter
      const error = searchParams.get('error');
      if (error) {
        const errorDesc = searchParams.get('error_description') || 'Authorization was denied';
        setStatus('error');
        setErrorMessage(errorDesc);
        return;
      }

      const code = searchParams.get('code');
      const state = searchParams.get('state');

      if (!code || !state) {
        setStatus('error');
        setErrorMessage('Missing authorization code or state parameter');
        return;
      }

      try {
        const response = await fetch('/api/auth/twitter2/callback', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ code, state }),
        });

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.error || 'Authentication failed');
        }

        setStatus('success');

        if (data.status === 'existing_user') {
          // Existing user - store token and redirect home
          const authData = {
            token: data.token,
            userId: data.user.id,
            displayName: data.user.display_name,
            authType: 'twitter',
            walletAddress: null,
            twitterHandle: data.user.twitter_handle,
            profileImageUrl: data.user.profile_image_url,
            expiresAt: Date.now() + (data.expires_in * 1000),
          };
          localStorage.setItem('mfersarcade_auth', JSON.stringify(authData));
          window.dispatchEvent(new CustomEvent('auth-storage-update'));

          setTimeout(() => navigate('/'), 500);

        } else if (data.status === 'new_user') {
          // New user - redirect to registration
          const params = new URLSearchParams({
            temp_token: data.temp_token,
            auth_type: 'twitter',
            identifier: data.identifier,
            suggested_name: data.suggested_name,
            twitter_handle: data.twitter_handle || '',
            profile_image_url: data.profile_image_url || '',
          });
          setTimeout(() => navigate(`/register?${params.toString()}`), 500);
        }

      } catch (err) {
        setStatus('error');
        setErrorMessage(err instanceof Error ? err.message : 'Authentication failed');
      }
    };

    processCallback();
  }, [searchParams, navigate, hasProcessed]);

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        {status === 'loading' && (
          <>
            <div style={styles.spinner} />
            <p style={styles.text}>Signing in with X...</p>
          </>
        )}

        {status === 'success' && (
          <>
            <div style={styles.successIcon}>OK</div>
            <p style={styles.text}>Success! Redirecting...</p>
          </>
        )}

        {status === 'error' && (
          <>
            <div style={styles.errorIcon}>X</div>
            <p style={styles.errorText}>{errorMessage}</p>
            <button style={styles.button} onClick={() => navigate('/')}>
              Back to Home
            </button>
          </>
        )}
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#1a1a2e',
  },
  card: {
    backgroundColor: '#252542',
    borderRadius: '16px',
    padding: '48px',
    textAlign: 'center',
    minWidth: '280px',
  },
  spinner: {
    width: '48px',
    height: '48px',
    border: '4px solid #333',
    borderTop: '4px solid #fff',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
    margin: '0 auto 24px',
  },
  successIcon: {
    fontSize: '48px',
    color: '#4ade80',
    marginBottom: '16px',
    fontWeight: 'bold',
  },
  errorIcon: {
    fontSize: '48px',
    color: '#ef4444',
    marginBottom: '16px',
    fontWeight: 'bold',
  },
  text: {
    color: '#fff',
    fontSize: '16px',
    margin: 0,
  },
  errorText: {
    color: '#ef4444',
    fontSize: '16px',
    marginBottom: '24px',
  },
  button: {
    backgroundColor: '#e94560',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    padding: '12px 24px',
    fontSize: '14px',
    fontWeight: 'bold',
    cursor: 'pointer',
  },
};

export default TwitterCallback;
